import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as React from "react";
import { pdf } from "@react-pdf/renderer";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { clsx } from "clsx";
import { FaPlus } from "react-icons/fa";
import { twMerge } from "tailwind-merge";
import {
  Search,
  Download,
  BarChart3,
  List,
  MoreHorizontal,
  UserPlus,
  
  RotateCcw,
  Trash2,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  CircleCheckBig,
} from "lucide-react";
import { useAlert } from "components/UI_components/Alert";
import NoDataIcon from "assets/empty state/NoItems.svg";
import { SnagStatsCards } from "./SnagStatsCards";
import { SnagDetailView } from "./SnagDetailView";
import { SnagReporting } from "./SnagReporting";
import { CreateSnagDrawer } from "./CreateSnagDrawer";
import ConfirmActionAlert from "../ConfirmActionAlert";
import AssignMembersModal from "./AssignMembersModal";
import SnagsPDFDocument from "./SnagsPDFDocument";
import { IoBan } from "react-icons/io5";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-sm-primary text-primary-foreground hover:bg-sm-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-sm-accent hover:text-white",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

const Button = React.forwardRef((props, ref) => {
  const { className, variant, size, asChild = false, ...rest } = props;
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size, className }))}
      {...rest}
    />
  );
});
Button.displayName = "Button";

const Input = React.forwardRef((props, ref) => {
  const { className, type, ...rest } = props;
  return (
    <input
      ref={ref}
      type={type}
      className={cn(
        "flex h-10 w-full rounded-md border border-input bg-background py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...rest}
    />
  );
});
Input.displayName = "Input";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-sm-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground",
        outline: "text-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

function Badge(props) {
  const { className, variant, ...rest } = props;
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...rest} />
  );
}

const Checkbox = React.forwardRef(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer h-4 w-4 shrink-0 rounded-sm border border-sm-primary ring-offset-background data-[state=checked]:bg-sm-primary data-[state=checked]:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
      <Check className="h-4 w-4" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = "Checkbox";

const Select = SelectPrimitive.Root;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  ),
);
SelectTrigger.displayName = "SelectTrigger";

const SelectScrollUpButton = React.forwardRef(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollUpButton
      ref={ref}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
  ),
);
SelectScrollUpButton.displayName = "SelectScrollUpButton";

const SelectScrollDownButton = React.forwardRef(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollDownButton
      ref={ref}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownButton>
  ),
);
SelectScrollDownButton.displayName = "SelectScrollDownButton";

const SelectContent = React.forwardRef(
  ({ className, children, position = "popper", ...props }, ref) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        sideOffset={6}
        collisionPadding={12}
        className={cn(
          "z-[9999] max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-sm-border bg-background text-popover-foreground shadow-xl",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
          className,
        )}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport className="p-1">
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  ),
);
SelectContent.displayName = "SelectContent";

const SelectItem = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "focus:bg-accent focus:text-accent-foreground",
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  ),
);
SelectItem.displayName = "SelectItem";

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuContent = React.forwardRef(
  ({ className, sideOffset = 6, ...props }, ref) => (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-[9999] min-w-[10rem] overflow-hidden rounded-md border border-sm-border bg-background p-1 shadow-xl",
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  ),
);

DropdownMenuContent.displayName = "DropdownMenuContent";

const DropdownMenuItem = React.forwardRef(
  ({ className, inset, ...props }, ref) => (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus:bg-accent focus:text-accent-foreground",
        inset && "pl-8",
        className,
      )}
      {...props}
    />
  ),
);
DropdownMenuItem.displayName = "DropdownMenuItem";

const statusColors = {
  Draft: "bg-gray-bg text-text-gray border border-sm-border",
  Open: "bg-sm-card text-[#3b0069] border border-sm-border",
  "Pending Approval": "bg-task-hover text-text-clr border border-sm-border",
  "Issue Raised": "bg-btn-bg-2/15 text-text-clr border border-sm-border",
  "Not Planned": "bg-gray-bg text-text-gray border border-sm-border",
  Resolved: "bg-light-green text-dark-green border border-transparent",
  Closed: "bg-light-green text-dark-green border border-transparent",
  Rejected: "bg-dark-red/10 text-dark-red border border-transparent",
};

const priorityColors = {
  Critical: "bg-dark-red/10 text-dark-red border border-transparent",
  High: "bg-btn-bg-2/15 text-text-clr border border-transparent",
  Medium: "bg-task-hover text-text-clr border border-transparent",
  Low: "bg-gray-bg text-text-gray border border-sm-border",
};

const STATUSES = [
  "Draft",
  "Open",
  "Pending Approval",
  "Issue Raised",
  "Not Planned",
  "Resolved",
  "Closed",
  "Rejected",
];
const PRIORITIES = ["Critical", "High", "Medium", "Low"];
const API_BASE_URL = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");

const PRIORITY_NUM_TO_LABEL = {
  0: "Critical",
  1: "High",
  2: "Medium",
  3: "Low",
};

const toTitleCase = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const normalizePriority = (priority) => {
  if (priority === null || priority === undefined) return "Medium";
  if (typeof priority === "number") {
    return PRIORITY_NUM_TO_LABEL[priority] || "Medium";
  }
  const normalized = toTitleCase(priority);
  return PRIORITIES.includes(normalized) ? normalized : "Medium";
};

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const getCurrentUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const id = user?.user ?? user?.id ?? user?.user_id ?? user?.pk;
    const numericId = Number(id);
    return Number.isFinite(numericId) ? numericId : null;
  } catch {
    return null;
  }
};

const hasAdminAccess = (member) => {
  if (!member) return false;
  if (member?.is_admin) return true;

  const roleText = [
    member?.joined_as,
    member?.role,
    member?.profession,
    member?.user?.role,
    member?.user?.profession,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return roleText.includes("owner") || roleText.includes("admin");
};

const normalizeStatus = (status) => {
  if (!status) return "Open";
  const normalized = toTitleCase(status);
  if (STATUSES.includes(normalized)) return normalized;
  if (normalized === "In Progress") return "Open";
  if (normalized === "Not Planed") return "Not Planned";
  if (normalized === "Notplanned") return "Not Planned";
  return normalized;
};

const getAssignedMemberName = (member, index = 0) => {
  if (typeof member === "string") return member.trim();
  return (
    member?.assignee_name ||
    member?.name ||
    member?.full_name ||
    `${member?.user?.first_name || ""} ${member?.user?.last_name || ""}`.trim() ||
    `Member ${index + 1}`
  );
};

const getAssignedMemberAvatar = (member) => {
  const rawUrl =
    member?.assignee_userpic ||
    member?.assignee_profile_pic ||
    member?.userpic ||
    member?.profile_pic ||
    member?.profilePic ||
    member?.avatar ||
    member?.image ||
    member?.user?.userpic ||
    member?.user?.profile_pic ||
    member?.user?.avatar ||
    "";

  if (!rawUrl || typeof rawUrl !== "string") return "";
  if (/^(https?:\/\/|blob:|data:)/i.test(rawUrl)) return rawUrl;
  if (!API_BASE_URL) return rawUrl;
  if (rawUrl.startsWith("/")) return `${API_BASE_URL}${rawUrl}`;
  return `${API_BASE_URL}/${rawUrl}`;
};

const normalizeAssignedMembers = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((member, index) => {
      const name = getAssignedMemberName(member, index);
      return {
        name,
        avatarUrl: getAssignedMemberAvatar(member),
      };
    })
    .filter((member) => Boolean(member.name));
};

const normalizeAssignedTo = (raw) => {
  return normalizeAssignedMembers(raw).map((member) => member.name);
};

const normalizeSnag = (snag, index) => {
  const id = String(snag?.id ?? snag?.snag_id ?? `snag-${index + 1}`);
  return {
    id,
    code:
      snag?.code ||
      snag?.snag_code ||
      `SNG-${String(snag?.id ?? index + 1).padStart(3, "0")}`,
    title: snag?.title || `Snag ${index + 1}`,
    description: snag?.description || "",
    project:
      snag?.project_name ||
      snag?.project?.name ||
      snag?.site_name ||
      snag?.site?.site_name ||
      "Unassigned",
    location: snag?.location || "N/A",
    category: snag?.category || "Other",
    subcategory: snag?.subcategory || "",
    siteId: snag?.site || snag?.site_id || snag?.site?.id || null,
    priority: normalizePriority(snag?.priority),
    status: normalizeStatus(snag?.status),
    createdBy: snag?.created_by || "Unknown",
    createdAt:
      snag?.created_at?.slice?.(0, 10) ||
      snag?.created_on?.slice?.(0, 10) ||
      "",
    assignedTo: normalizeAssignedTo(snag?.assignees),
    assignedMembers: normalizeAssignedMembers(snag?.assignees),
    expectedClosure:
      snag?.expected_closure_date?.slice?.(0, 10) ||
      snag?.expectedClosure ||
      "",
    image: snag?.issue_image || snag?.image || "/placeholder.svg",
    resolvedImage:
      snag?.resolved_image ||
      snag?.resolution_image ||
      snag?.after_image ||
      snag?.fixed_image ||
      "",
    attachments: Array.isArray(snag?.attachments) ? snag.attachments : [],
    activities: Array.isArray(snag?.activities) ? snag.activities : [],
    comments: Array.isArray(snag?.comments) ? snag.comments : [],
  };
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const unwrapObject = (payload) => {
  if (payload && typeof payload === "object" && !Array.isArray(payload?.data)) {
    return payload?.data || payload;
  }
  return payload;
};

const getSafeLocalObject = (key) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : {};
  } catch {
    return {};
  }
};

const toAbsoluteMediaUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (/^(https?:\/\/|blob:|data:)/i.test(url)) return url;
  if (!API_BASE_URL) return url;
  if (url.startsWith("/")) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/${url}`;
};

const pickMediaUrl = (entry) => {
  if (!entry) return "";
  if (typeof entry === "string") return toAbsoluteMediaUrl(entry);

  const candidates = [
    entry?.url,
    entry?.file,
    entry?.image,
    entry?.image_url,
    entry?.attachment,
    entry?.attachment_url,
    entry?.resolved_image,
    entry?.resolution_image,
    entry?.after_image,
  ];

  const match = candidates.find(
    (value) => typeof value === "string" && value.trim().length > 0,
  );

  return match ? toAbsoluteMediaUrl(match) : "";
};

const isImageFile = (url) => {
  if (!url) return false;
  return /\.(jpg|jpeg|png|gif|svg|webp|bmp|avif)($|\?)/i.test(url);
};

const fileToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () =>
      resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () =>
      reject(reader.error || new Error("Failed to read image file"));
    reader.readAsDataURL(blob);
  });

const fetchPdfImageSource = async (url, token, cache) => {
  const absoluteUrl = pickMediaUrl(url);
  if (
    !absoluteUrl ||
    absoluteUrl === "/placeholder.svg" ||
    absoluteUrl.endsWith("/placeholder.svg")
  ) {
    return "";
  }

  if (cache?.has(absoluteUrl)) {
    return cache.get(absoluteUrl);
  }

  const request = (async () => {
    if (/^data:/i.test(absoluteUrl)) return absoluteUrl;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(absoluteUrl, { headers });
      if (!response.ok) return absoluteUrl;

      const blob = await response.blob();
      if (blob?.type && !blob.type.startsWith("image/")) {
        return "";
      }

      return await fileToDataUrl(blob);
    } catch {
      return absoluteUrl;
    }
  })();

  cache?.set(absoluteUrl, request);
  const result = await request;
  cache?.set(absoluteUrl, result);
  return result;
};

const normalizeAssignedNames = (raw) =>
  toArray(raw)
    .map((member, index) => {
      if (typeof member === "string") return member;

      return (
        member?.assignee_name ||
        member?.label ||
        member?.name ||
        member?.full_name ||
        `${member?.user?.first_name || ""} ${member?.user?.last_name || ""}`.trim() ||
        (member?.user ? `Member ${index + 1}` : "")
      );
    })
    .map((name) => String(name || "").trim())
    .filter(Boolean);

const normalizeActivitiesForReport = (payload) =>
  unwrapList(payload).map((activity, index) => {
    const nestedAttachment = toArray(activity?.attachments).find((item) =>
      Boolean(pickMediaUrl(item)),
    );

    return {
      id: String(activity?.id ?? activity?.activity_id ?? `act-${index + 1}`),
      type:
        activity?.event_type || activity?.type || activity?.action || "update",
      typeLabel: toTitleCase(
        activity?.event_type || activity?.type || activity?.action || "update",
      ),
      user:
        activity?.created_by ||
        activity?.user_name ||
        activity?.user?.full_name ||
        activity?.user?.name ||
        activity?.performed_by_name ||
        "User",
      timestamp:
        activity?.created_on ||
        activity?.created_at ||
        activity?.timestamp ||
        "",
      text:
        activity?.event_remarks ||
        activity?.description ||
        activity?.message ||
        activity?.text ||
        activity?.action ||
        "Activity updated",
      attachment:
        pickMediaUrl(activity?.related_attachment || activity?.attachment) ||
        pickMediaUrl(nestedAttachment),
    };
  });

const normalizeAttachmentsForReport = (payload) =>
  unwrapList(payload).map((attachment, index) => ({
    id: String(
      attachment?.id ?? attachment?.attachment_id ?? `att-${index + 1}`,
    ),
    file: pickMediaUrl(attachment?.file || attachment?.url || attachment),
    name:
      attachment?.name ||
      attachment?.label ||
      attachment?.filename ||
      attachment?.file_name ||
      "",
    uploadedBy:
      attachment?.uploaded_by ||
      attachment?.created_by ||
      attachment?.user_name ||
      "User",
    uploadedOn:
      attachment?.uploaded_on ||
      attachment?.uploaded_at ||
      attachment?.created_at ||
      attachment?.created_on ||
      "",
  }));

const normalizeResolvedImage = (detail, baseSnag = {}) => {
  const directCandidates = [
    detail?.resolved_image,
    detail?.resolution_image,
    detail?.after_image,
    detail?.fixed_image,
    detail?.proof_image,
    detail?.approval_image,
    detail?.issue_after_image,
    detail?.resolved_issue_image,
    baseSnag?.resolvedImage,
    baseSnag?.resolved_image,
  ];

  for (const candidate of directCandidates) {
    const url = pickMediaUrl(candidate);
    if (url) return url;
  }

  const attachmentGroups = [
    ...toArray(detail?.attachments),
    ...toArray(detail?.snag_attachments),
    ...toArray(detail?.resolved_attachments),
  ];
  const resolvedAttachment = attachmentGroups.find((item) =>
    /resolve|approval|fix|after/i.test(
      String(item?.status || item?.type || ""),
    ),
  );
  const anyAttachment = attachmentGroups.find((item) => pickMediaUrl(item));

  const activityAttachments = toArray(detail?.activities)
    .flatMap((activity) => toArray(activity?.attachments))
    .filter(Boolean);
  const resolvedActivityAttachment = activityAttachments.find((item) =>
    /resolve|approval|fix|after/i.test(
      String(item?.status || item?.type || ""),
    ),
  );
  const anyActivityAttachment = activityAttachments.find((item) =>
    pickMediaUrl(item),
  );

  return (
    pickMediaUrl(resolvedAttachment) ||
    pickMediaUrl(anyAttachment) ||
    pickMediaUrl(resolvedActivityAttachment) ||
    pickMediaUrl(anyActivityAttachment) ||
    ""
  );
};

const mergeSnagDetailForReport = (baseSnag, detail, extras = {}) => {
  const activities = Array.isArray(extras?.activities)
    ? extras.activities
    : Array.isArray(baseSnag?.activities)
      ? baseSnag.activities
      : [];
  const attachments = Array.isArray(extras?.attachments)
    ? extras.attachments
    : Array.isArray(baseSnag?.attachments)
      ? baseSnag.attachments
      : [];

  return {
    ...baseSnag,
    id: String(detail?.id ?? baseSnag?.id ?? ""),
    code:
      detail?.code ||
      detail?.snag_code ||
      baseSnag?.code ||
      `SNG-${String(detail?.id || baseSnag?.id || "").padStart(3, "0")}`,
    title: detail?.title || baseSnag?.title || "Snag",
    description: detail?.description ?? baseSnag?.description ?? "",
    project:
      detail?.project_name ||
      detail?.project?.name ||
      detail?.site_name ||
      detail?.site?.site_name ||
      baseSnag?.project ||
      "Unassigned",
    location: detail?.location || baseSnag?.location || "N/A",
    category: detail?.category || baseSnag?.category || "Other",
    subcategory: detail?.subcategory || baseSnag?.subcategory || "",
    siteId:
      detail?.site ||
      detail?.site_id ||
      detail?.site?.id ||
      baseSnag?.siteId ||
      null,
    priority: normalizePriority(detail?.priority ?? baseSnag?.priority),
    status: normalizeStatus(detail?.status ?? baseSnag?.status),
    createdBy: detail?.created_by || baseSnag?.createdBy || "Unknown",
    createdAt:
      detail?.created_at?.slice?.(0, 10) ||
      detail?.created_on?.slice?.(0, 10) ||
      baseSnag?.createdAt ||
      "",
    expectedClosure:
      detail?.expected_closure_date?.slice?.(0, 10) ||
      baseSnag?.expectedClosure ||
      "",
    image:
      pickMediaUrl(detail?.issue_image) || pickMediaUrl(baseSnag?.image) || "",
    resolvedImage: normalizeResolvedImage(
      { ...detail, attachments, activities },
      baseSnag,
    ),
    attachments,
    activities,
    assignedTo:
      extras?.assignedTo?.length > 0
        ? extras.assignedTo
        : normalizeAssignedNames(detail?.assignees).length > 0
          ? normalizeAssignedNames(detail?.assignees)
          : baseSnag?.assignedTo || [],
  };
};

const fetchJsonWithFallback = async (paths, token) => {
  let lastError = "";

  for (const path of paths) {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json().catch(() => ({}));

    if (response.ok) {
      return data;
    }

    lastError = data?.detail || data?.message || `${response.status}`;
  }

  throw new Error(lastError || "Failed to fetch report data");
};

const fetchOptionalJson = async (path, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return null;
    return await response.json().catch(() => null);
  } catch {
    return null;
  }
};

const hydrateSnagForExport = async ({
  snag,
  organizationId,
  token,
  imageCache,
}) => {
  try {
    const detailPayload = await fetchJsonWithFallback(
      [
        `/snag-management/org/${organizationId}/snags/${snag.id}/`,
        `/snag-management/org/${organizationId}/snags/get-a-snag/${snag.id}/`,
      ],
      token,
    );

    const [activitiesPayload, attachmentsPayload, assignedPayload] =
      await Promise.all([
        fetchOptionalJson(
          `/snag-management/org/${organizationId}/snags/${snag.id}/activities/`,
          token,
        ),
        fetchOptionalJson(
          `/snag-management/org/${organizationId}/snags/${snag.id}/attachments/`,
          token,
        ),
        fetchOptionalJson(
          `/snag-management/org/${organizationId}/snags/${snag.id}/assign/`,
          token,
        ),
      ]);

    const activities = normalizeActivitiesForReport(activitiesPayload);
    const attachments = normalizeAttachmentsForReport(attachmentsPayload);
    const assignedTo = normalizeAssignedNames(
      Array.isArray(assignedPayload)
        ? assignedPayload
        : assignedPayload?.data || assignedPayload?.results || [],
    );

    const merged = mergeSnagDetailForReport(snag, unwrapObject(detailPayload), {
      activities,
      attachments,
      assignedTo,
    });

    const [imageSource, resolvedImageSource, hydratedActivities] =
      await Promise.all([
        fetchPdfImageSource(merged.image, token, imageCache),
        fetchPdfImageSource(merged.resolvedImage, token, imageCache),
        Promise.all(
          merged.activities.map(async (activity) => ({
            ...activity,
            attachmentSource:
              activity?.attachment && isImageFile(activity.attachment)
                ? await fetchPdfImageSource(
                    activity.attachment,
                    token,
                    imageCache,
                  )
                : "",
          })),
        ),
      ]);

    return {
      ...merged,
      imageSource,
      resolvedImageSource,
      activities: hydratedActivities,
    };
  } catch {
    const fallback = mergeSnagDetailForReport(snag, snag, {
      activities: normalizeActivitiesForReport(snag?.activities),
      attachments: normalizeAttachmentsForReport(snag?.attachments),
      assignedTo: normalizeAssignedNames(snag?.assignedTo),
    });

    return {
      ...fallback,
      imageSource: await fetchPdfImageSource(fallback.image, token, imageCache),
      resolvedImageSource: await fetchPdfImageSource(
        fallback.resolvedImage,
        token,
        imageCache,
      ),
    };
  }
};

const getCodeNumber = (snag) => {
  const fromCode = Number(String(snag?.code || "").replace(/\D+/g, ""));
  if (Number.isFinite(fromCode) && fromCode > 0) return fromCode;
  const fromId = Number(snag?.id);
  if (Number.isFinite(fromId)) return fromId;
  return 0;
};

const sanitizeFilePart = (value) =>
  String(value || "")
    .replace(/[\\/:"*?<>|]+/g, "-")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

export default function SnagsDashboard({
  organizationId,
  siteId,
  token: tokenProp,
  isAdmin,
  currentMember,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlSnagId = searchParams.get("snagId");
  const fromApprovalInbox = searchParams.get("fromApprovalInbox") === "true";
  const { addAlert } = useAlert();
  const [view, setView] = useState("list");
  const [viewMode, setViewMode] = useState(isAdmin ? "admin" : "assignee");
  const [, setCanViewAdmin] = useState(isAdmin);
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedSnag, setSelectedSnag] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const token = tokenProp || localStorage.getItem("access");

  const [snagsData, setSnagsData] = useState([]);
  const [loadingSnags, setLoadingSnags] = useState(true);
  const [snagsError, setSnagsError] = useState("");

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [deleteAlertState, setDeleteAlertState] = useState({
    isOpen: false,
    itemIds: null,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [canEditSnag, setCanEditSnag] = useState(
    currentMember?.joined_as === "owner" ||
      currentMember?.snag_admin === true ||
      localStorage.getItem("isOwner") === "true",
  );

  useEffect(() => {
    if (urlSnagId && snagsData.length > 0 && !selectedSnag) {
      const snagToSelect = snagsData.find(
        (s) => String(s.id) === String(urlSnagId),
      );
      if (snagToSelect) {
        setSelectedSnag(snagToSelect);
      }
    }
  }, [urlSnagId, snagsData, selectedSnag]);

  const fetchSnags = async () => {
    if (!organizationId || !token || !API_BASE_URL) return;

    setLoadingSnags(true);
    setSnagsError("");

    try {
      const endpoints = [
        `/snag-management/org/${organizationId}/snags/get-all-snags/`,
      ];

      let payload = null;
      let lastErr = "";

      for (const path of endpoints) {
        const res = await fetch(`${API_BASE_URL}${path}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          payload = data;
          break;
        }
        lastErr = data?.detail || data?.message || `${res.status}`;
      }

      if (!payload) {
        throw new Error(lastErr || "Failed to fetch snags");
      }

      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.results)
            ? payload.results
            : [];

      setSnagsData(list.map(normalizeSnag));
    } catch (e) {
      setSnagsError(e.message || "Failed to fetch snags");
    } finally {
      setLoadingSnags(false);
    }
  };

  useEffect(() => {
    fetchSnags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, token]);

  useEffect(() => {
    let isMounted = true;

    const resolveViewPermission = async () => {
      if (!organizationId || !token || !API_BASE_URL) {
        if (isMounted) {
          setCanViewAdmin(false);
          setViewMode("assignee");
        }
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/org/organization/${organizationId}/members/`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error("Failed to fetch members");

        const members = unwrapList(payload);
        const currentUserId = getCurrentUserId();
        const currentMember = members.find((member) => {
          const memberUserIds = [
            member?.user_id,
            member?.user?.id,
            member?.member_user_id,
            member?.user?.user_id,
          ]
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id));
          return (
            currentUserId !== null && memberUserIds.includes(currentUserId)
          );
        });

        const adminAllowed =
          hasAdminAccess(currentMember) ||
          localStorage.getItem("isOwner") === "true";
        const editAllowed =
          String(currentMember?.joined_as || "").toLowerCase() === "owner" ||
          currentMember?.snag_admin === true ||
          localStorage.getItem("isOwner") === "true";

        if (isMounted) {
          setCanViewAdmin(adminAllowed);
          setCanEditSnag(editAllowed);
          setViewMode((prev) => (adminAllowed ? prev : "assignee"));
        }
      } catch {
        if (isMounted) {
          const ownerFallback = localStorage.getItem("isOwner") === "true";
          setCanViewAdmin(ownerFallback);
          setCanEditSnag(ownerFallback);
          setViewMode((prev) => (ownerFallback ? prev : "assignee"));
        }
      }
    };

    resolveViewPermission();

    return () => {
      isMounted = false;
    };
  }, [organizationId, token]);

  const handleSnagCreated = (newSnag) => {
    setSnagsData((prev) => [normalizeSnag(newSnag, 0), ...prev]);
    fetchSnags();
  };

  const handleSnagUpdated = (updatedSnag) => {
    setSnagsData((prev) =>
      prev.map((item) =>
        String(item.id) === String(updatedSnag.id) ? updatedSnag : item,
      ),
    );
    setSelectedSnag(updatedSnag);
  };

  const handleSnagDeleted = (snagId) => {
    setSnagsData((prev) =>
      prev.filter((item) => String(item.id) !== String(snagId)),
    );
    setSelectedSnag(null);
  };

  const allSnagsSorted = useMemo(
    () => [...snagsData].sort((a, b) => getCodeNumber(b) - getCodeNumber(a)),
    [snagsData],
  );

  const snags = useMemo(() => {
    const filtered = snagsData.filter((s) => {
      if (
        search &&
        !s.title.toLowerCase().includes(search.toLowerCase()) &&
        !s.code.toLowerCase().includes(search.toLowerCase())
      ) {
        return false;
      }
      if (projectFilter !== "all" && s.project !== projectFilter) return false;
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (categoryFilter !== "all" && s.category !== categoryFilter)
        return false;
      if (priorityFilter !== "all" && s.priority !== priorityFilter)
        return false;
      return true;
    });

    return [...filtered].sort((a, b) => getCodeNumber(b) - getCodeNumber(a));
  }, [
    snagsData,
    search,
    projectFilter,
    statusFilter,
    categoryFilter,
    priorityFilter,
  ]);

  const projects = useMemo(() => {
    return [...new Set(snagsData.map((s) => s.project).filter(Boolean))];
  }, [snagsData]);
  const categories = useMemo(() => {
    return [...new Set(snagsData.map((s) => s.category).filter(Boolean))];
  }, [snagsData]);

  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAll = () =>
    setSelected(selected.length === snags.length ? [] : snags.map((s) => s.id));

  const clearFilters = () => {
    setSearch("");
    setProjectFilter("all");
    setStatusFilter("all");
    setCategoryFilter("all");
    setPriorityFilter("all");
  };

  const handleUpdateStatusMultiple = async (status, itemIds = selected) => {
    if (!itemIds || itemIds.length === 0) return;
    setActionLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/snag-management/org/${organizationId}/snags/update-status/multiple/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            snag_ids: itemIds,
            status: status,
          }),
        },
      );
      if (!res.ok) throw new Error("Failed to update status");
      setSelected([]);
      fetchSnags();
    } catch (err) {
      setSnagsError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteMultiple = async () => {
    const itemIds = deleteAlertState.itemIds || selected;
    if (!itemIds || itemIds.length === 0) return;
    setActionLoading(true);
    try {
      for (const pk of itemIds) {
        await fetch(
          `${API_BASE_URL}/snag-management/org/${organizationId}/snags/${pk}/`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      }
      setSelected([]);
      setDeleteAlertState({ isOpen: false, itemIds: null });
      fetchSnags();
    } catch (err) {
      setSnagsError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportSnagsReport = async () => {
    if (exportingPdf) return;

    if (!organizationId || !token || !API_BASE_URL) {
      addAlert("Missing organization details for export", "error");
      return;
    }

    if (allSnagsSorted.length === 0) {
      addAlert("No snags available to export", "warning");
      return;
    }

    setExportingPdf(true);
    setSnagsError("");

    try {
      const imageCache = new Map();
      const organization = getSafeLocalObject("organization");
      const logoSource = await fetchPdfImageSource(
        organization?.logo_image_file || organization?.logo_image,
        token,
        imageCache,
      );

      const detailedSnags = await Promise.all(
        allSnagsSorted.map((snag) =>
          hydrateSnagForExport({
            snag,
            organizationId,
            token,
            imageCache,
          }),
        ),
      );

      const pdfDocument = (
        <SnagsPDFDocument
          organization={{ ...organization, logoSource }}
          generatedAt={new Date().toISOString()}
          snags={detailedSnags}
        />
      );

      const blob = await pdf(pdfDocument).toBlob();
      const reportName = sanitizeFilePart(
        organization?.organization_name || `organization_${organizationId}`,
      );
      const reportDate = new Date().toISOString().slice(0, 10);
      const fileName = `SiteMaster_Snags_Report_${reportName}_${reportDate}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      addAlert("Snags report downloaded successfully", "success");
    } catch (error) {
      console.error("Failed to export snags report:", error);
      setSnagsError(error?.message || "Failed to export snags report");
      addAlert(error?.message || "Failed to export snags report", "error");
    } finally {
      setExportingPdf(false);
    }
  };

  if (selectedSnag) {
    return (
      <SnagDetailView
        snag={selectedSnag}
        onBack={() => {
          if (fromApprovalInbox) {
            navigate("/dashboard/home?focusApprovalInbox=true");
            return;
          }
          setSelectedSnag(null);
          if (searchParams.has("snagId")) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("snagId");
            setSearchParams(newParams, { replace: true });
          }
        }}
        viewMode={viewMode}
        isAdmin={isAdmin}
        organizationId={organizationId}
        token={token}
        canEditSnag={canEditSnag}
        onSnagUpdated={handleSnagUpdated}
        onSnagDeleted={handleSnagDeleted}
      />
    );
  }

  return (
    <div className="w-full space-y-3">
      <div className="sticky top-0 z-20 pb-2 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Snags</h1>
            <p className="text-sm text-muted-foreground">
              Manage project snags, defects, and quality issues
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* <div className="flex rounded-full bg-sm-card p-0.5">
              {canViewAdmin && (
                <button
                  onClick={() => setViewMode("admin")}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${
                    isAdmin
                      ? "bg-sm-primary text-white shadow-sm"
                      : "text-text-gray hover:text-primary-text"
                  }`}
                >
                  Admin View
                </button>
              )}
              <button
                onClick={() => setViewMode("assignee")}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${
                  viewMode === "assignee"
                    ? "bg-sm-primary text-white shadow-sm"
                    : "text-text-gray hover:text-primary-text"
                }`}
              >
                Assignee View
              </button>
            </div> */}

            {/* <div className="h-6 w-px bg-sm-border" /> */}

            <div className="flex rounded-lg bg-gray-bg p-0.5">
              <button
                onClick={() => setView("list")}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  view === "list" ? "bg-background shadow-sm" : ""
                }`}
              >
                <List className="h-4 w-4 text-text-gray" />
              </button>
              <button
                onClick={() => setView("reports")}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  view === "reports" ? "bg-background shadow-sm" : ""
                }`}
              >
                <BarChart3 className="h-4 w-4 text-text-gray" />
              </button>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-9 border-sm-border px-3 text-sm text-primary-text"
              onClick={handleExportSnagsReport}
              disabled={
                exportingPdf || loadingSnags || allSnagsSorted.length === 0
              }
            >
              {exportingPdf ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Preparing
                  PDF...
                </>
              ) : (
                <>
                  <Download className="mr-1 h-4 w-4" /> Export
                </>
              )}
            </Button>

            {isAdmin && (
              <Button
                onClick={() => setCreateOpen(true)}
                className="h-9 flex items-center gap-2 bg-[#3B0069] text-white px-3 rounded-lg text-sm hover:!bg-purple-700 transition-colors duration-200"
              >
                <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <FaPlus className="w-2.5 h-2.5" />
                </span>
                <span>Create Snag</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      <SnagStatsCards snags={snagsData} />

      {view === "reports" ? (
        <SnagReporting snags={snagsData} />
      ) : (
        <>
          <div className="sticky top-[68px] z-10 flex flex-wrap items-center gap-2 bg-background/95 py-2 backdrop-blur-sm">
            <div className="relative min-w-[300px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-gray" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search snags..."
                className="h-9 border-sm-border bg-background pl-10 text-sm"
              />
            </div>

            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="h-9 w-[160px] border-sm-border bg-background text-sm">
                <SelectValue placeholder="Project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-[145px] border-sm-border bg-background text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-9 w-[155px] border-sm-border bg-background text-sm">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="h-9 w-[135px] border-sm-border bg-background text-sm">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-3 text-sm text-text-gray hover:bg-task-hover hover:text-primary-text"
              onClick={clearFilters}
            >
              Clear
            </Button>
          </div>

          {selected.length > 0 && (
            <div className="animate-fade-in flex items-center gap-2 rounded-lg border border-gray-dark bg-sm-card px-3 py-2">
              <span className="text-sm font-medium text-text-clr">
                {selected.length} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-sm-border text-xs"
                onClick={() => setShowAssignModal(true)}
                disabled={actionLoading}
              >
                <UserPlus className="mr-1 h-3.5 w-3.5" />
                Assign
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-sm-border text-xs"
                onClick={() => handleUpdateStatusMultiple("Resolved")}
                disabled={actionLoading}
              >
                <CircleCheckBig className="mr-1 h-3.5 w-3.5" />
                Resolve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-sm-border text-xs"
                onClick={() => handleUpdateStatusMultiple("Open")}
                disabled={actionLoading}
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                Re-open
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-sm-border text-xs"
                onClick={() => handleUpdateStatusMultiple("Not Planned")}
                disabled={actionLoading}
              >
                <IoBan className="mr-1 h-3.5 w-3.5" />
                Not Planned
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-sm-border text-xs text-dark-red"
                onClick={() =>
                  setDeleteAlertState({ isOpen: true, itemIds: selected })
                }
                disabled={actionLoading}
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" />
                Delete
              </Button>
            </div>
          )}

          <div className="w-full rounded-xl border border-sm-border bg-background">
            <div
              className="w-full overflow-x-auto overflow-y-auto"
              style={{ maxHeight: "calc(100vh - 380px)" }}
            >
              {snagsError && (
                <div className="border-b border-dark-red/20 bg-dark-red/10 px-4 py-3 text-sm text-dark-red">
                  {snagsError}
                </div>
              )}
              <table className="w-full text-sm bg-white">
                <thead className="sticky top-0 z-10">
                  <tr className="border-b border-sm-border bg-gray-bg">
                    {isAdmin && (
                      <th className="w-10 px-3 py-2.5">
                        <Checkbox
                          checked={
                            selected.length === snags.length && snags.length > 0
                          }
                          onCheckedChange={toggleAll}
                        />
                      </th>
                    )}
                    {[
                      "Snag",
                      "Project",
                      "Location",
                      "Category",
                      "Assigned",
                      "Due Date",
                      "Priority",
                      "Status",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-text-gray"
                      >
                        {h}
                      </th>
                    ))}
                    <th className="w-10 px-3" />
                  </tr>
                </thead>
                <tbody>
                  {snags.map((snag) => {
                    const isOverdue =
                      snag.expectedClosure &&
                      new Date(snag.expectedClosure) < new Date() &&
                      !["Closed", "Resolved"].includes(snag.status);

                    return (
                      <tr
                        key={snag.id}
                        className="group cursor-pointer border-b border-gray-bg transition-colors hover:bg-gray-bg"
                        onClick={() => setSelectedSnag(snag)}
                      >
                        {isAdmin && (
                          <td
                            className="px-3 py-2.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={selected.includes(snag.id)}
                              onCheckedChange={() => toggleSelect(snag.id)}
                            />
                          </td>
                        )}

                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-sm-border bg-white p-0.5">
                              <img
                                src={snag.image || "/placeholder.svg"}
                                alt=""
                                className="h-full w-full rounded object-contain"
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="max-w-[280px] truncate text-sm font-medium leading-none text-primary-text md:text-sm">
                                {snag.title}
                              </p>
                              <p className="font-mono text-xs text-text-gray">
                                {snag.code}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-3 py-2.5 text-sm text-text-gray">
                          {snag.project.split(" ").slice(0, 2).join(" ")}
                        </td>

                        <td className="px-3 py-2.5">
                          <span className="text-sm text-text-gray">
                            {snag.location}
                          </span>
                        </td>

                        <td className="px-3 py-2.5">
                          <Badge
                            variant="outline"
                            className="border-sm-border text-xs font-normal text-text-clr"
                          >
                            {snag.category}
                          </Badge>
                        </td>

                        <td className="px-3 py-2.5">
                          {snag.assignedMembers?.length > 0 ||
                          snag.assignedTo?.length > 0 ? (
                            <div className="flex -space-x-1.5">
                              {(snag.assignedMembers?.length > 0
                                ? snag.assignedMembers
                                : snag.assignedTo.map((name) => ({
                                    name,
                                    avatarUrl: "",
                                  }))
                              )
                                .slice(0, 2)
                                .map((member, index) => (
                                  <div
                                    key={`${member.name}-${index}`}
                                    className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full border-2 border-background bg-sm-card text-[8px] font-bold text-text-clr"
                                    title={member.name}
                                  >
                                    {member.avatarUrl ? (
                                      <img
                                        src={member.avatarUrl}
                                        alt={member.name}
                                        className="h-full w-full object-cover"
                                      />
                                    ) : (
                                      member.name
                                        .split(" ")
                                        .map((w) => w[0])
                                        .join("")
                                    )}
                                  </div>
                                ))}
                              {(snag.assignedMembers?.length ||
                                snag.assignedTo.length) > 2 && (
                                <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-background bg-sm-card text-[8px] font-bold text-text-clr">
                                  +
                                  {(snag.assignedMembers?.length ||
                                    snag.assignedTo.length) - 2}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-text-gray">—</span>
                          )}
                        </td>

                        <td className="px-3 py-2.5">
                          <span
                            className={`text-sm ${
                              isOverdue
                                ? "font-medium text-dark-red"
                                : "text-text-gray"
                            }`}
                          >
                            {snag.expectedClosure || "—"}
                          </span>
                        </td>

                        <td className="px-3 py-2.5">
                          <Badge
                            variant="outline"
                            className={`${priorityColors[snag.priority] || ""} text-xs`}
                          >
                            {snag.priority}
                          </Badge>
                        </td>

                        <td className="px-3 py-2.5">
                          <Badge
                            variant="outline"
                            className={`${statusColors[snag.status] || ""} text-xs`}
                          >
                            {snag.status}
                          </Badge>
                        </td>

                        <td
                          className="px-3 py-2.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 opacity-0 transition-opacity hover:bg-gray-bg group-hover:opacity-100"
                              >
                                <MoreHorizontal className="h-4 w-4 text-text-gray" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-xs"
                                onClick={() => setSelectedSnag(snag)}
                              >
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-xs"
                                onClick={() => {
                                  setSelected([snag.id]);
                                  setShowAssignModal(true);
                                }}
                              >
                                Assign
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-xs"
                                onClick={() =>
                                  handleUpdateStatusMultiple("Resolved", [
                                    snag.id,
                                  ])
                                }
                              >
                                Mark Resolved
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-xs"
                                onClick={() =>
                                  handleUpdateStatusMultiple("Open", [snag.id])
                                }
                              >
                                Re-open
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-xs"
                                onClick={() =>
                                  handleUpdateStatusMultiple("Not Planned", [
                                    snag.id,
                                  ])
                                }
                              >
                                Not Planned
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-xs text-dark-red"
                                onClick={() =>
                                  setDeleteAlertState({
                                    isOpen: true,
                                    itemIds: [snag.id],
                                  })
                                }
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}

                  {!loadingSnags && snags.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-3 py-4 text-center">
                        <div className="flex flex-col items-center justify-center py-4">
                          <img
                            src={NoDataIcon}
                            alt="No Snags"
                            className="mb-4"
                            width={220}
                          />
                          <span className="text-gray-500 text-2xl">
                            No snags found
                          </span>
                          <span className="text-gray-500 text-sm">
                            Create one to get started.
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {loadingSnags && (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-3 py-12 text-center text-sm text-text-gray"
                      >
                        Loading snags...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      <CreateSnagDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSnagCreated={handleSnagCreated}
        token={token}
        orgId={organizationId}
        siteId={siteId}
      />
      <AssignMembersModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onAssign={() => {
          setSelected([]);
          fetchSnags();
        }}
        organizationId={organizationId}
        token={token}
        snagIds={selected}
      />
      <ConfirmActionAlert
        isOpen={deleteAlertState.isOpen}
        title="Delete Selected Snags"
        message={`Are you sure you want to delete ${deleteAlertState.itemIds?.length > 1 ? "these snags" : "this snag"}? This action cannot be undone.`}
        onConfirm={handleDeleteMultiple}
        onCancel={() => setDeleteAlertState({ isOpen: false, itemIds: null })}
        variant="danger"
        isLoading={actionLoading}
      />
    </div>
  );
}
